import React from "react";

import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/src/components/ui/button";
import { cn } from "@/src/lib/utils";

import { CONTROL_SURFACE_CLASS } from "./dataTableStyles";

import type { RowData } from "@tanstack/react-table";
import type { ChangeEvent, JSX, ReactNode } from "react";

import type { DataTableInstance } from "./dataTable.types";

const PAGE_SIZE_OPTION_LIST = [10, 25, 50, 100];

interface DataTablePaginationProps<TData extends RowData> {
  table: DataTableInstance<TData>;
  compact?: boolean;
}

interface PageButtonProps {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: PageButtonProps): JSX.Element {
  return (
    <Button
      aria-label={label}
      className={CONTROL_SURFACE_CLASS}
      disabled={disabled}
      onClick={onClick}
      size="icon"
      variant="outline"
    >
      {children}
    </Button>
  );
}

function DataTablePagination<TData extends RowData>({
  table,
  compact = false,
}: DataTablePaginationProps<TData>): JSX.Element | null {
  const { t } = useTranslation("table");
  const { pageIndex, pageSize } = table.atoms.pagination.get();

  const total = table.getRowCount();
  const from = total === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, total);
  const pageCount = Math.max(1, table.getPageCount());

  const pageSizeOptionList = PAGE_SIZE_OPTION_LIST.includes(pageSize)
    ? PAGE_SIZE_OPTION_LIST
    : [...PAGE_SIZE_OPTION_LIST, pageSize].sort(
        (firstSize, secondSize) => firstSize - secondSize,
      );

  if (compact && pageCount <= 1) return null;

  const handlePageSizeChange = (
    event: ChangeEvent<HTMLSelectElement>,
  ): void => {
    table.setPageSize(Number(event.target.value));
  };

  const pageControls = (
    <React.Fragment>
      <span className="font-medium">
        {t("dataTable.pageOf", { page: pageIndex + 1, pageCount })}
      </span>
      <div className="flex items-center gap-1">
        <PageButton
          disabled={!table.getCanPreviousPage()}
          label={t("dataTable.firstPage")}
          onClick={() => table.setPageIndex(0)}
        >
          <ChevronsLeft />
        </PageButton>
        <PageButton
          disabled={!table.getCanPreviousPage()}
          label={t("dataTable.previousPage")}
          onClick={() => table.previousPage()}
        >
          <ChevronLeft />
        </PageButton>
        <PageButton
          disabled={!table.getCanNextPage()}
          label={t("dataTable.nextPage")}
          onClick={() => table.nextPage()}
        >
          <ChevronRight />
        </PageButton>
        <PageButton
          disabled={!table.getCanNextPage()}
          label={t("dataTable.lastPage")}
          onClick={() => table.setPageIndex(pageCount - 1)}
        >
          <ChevronsRight />
        </PageButton>
      </div>
    </React.Fragment>
  );

  if (compact) {
    return (
      <div className="flex items-center justify-end gap-6 text-sm">
        {pageControls}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
      <div className="text-muted-foreground">
        {t("dataTable.range", { from, to, total })}
      </div>
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2">
          <span>{t("dataTable.rowsPerPage")}</span>
          {/* Select natif (accessible et testable), stylé comme les autres contrôles. */}
          <div className="relative">
            <select
              aria-label={t("dataTable.rowsPerPage")}
              onChange={handlePageSizeChange}
              value={pageSize}
              className={cn(
                CONTROL_SURFACE_CLASS,
                "focus-visible:border-ring focus-visible:ring-ring/50 h-8 appearance-none rounded-md border pr-8 pl-3 text-sm outline-none focus-visible:ring-[3px]",
              )}
            >
              {pageSizeOptionList.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ChevronDown className="text-muted-foreground pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2" />
          </div>
        </div>
        {pageControls}
      </div>
    </div>
  );
}

export default DataTablePagination;
